// B2B Leads Page
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLeads, formatTimeAgo, getInitials, getScoreClass } from '../utils/api';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'qualified', label: 'Qualified' },
  { key: 'booked', label: 'Booked' },
  { key: 'lost', label: 'Lost' }
];

export default function B2BLeads() {
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    loadLeads();
  }, [filter]);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      
      const data = await getLeads(params);
      setLeads(data.leads || []);
      
      // Calculate counts per status
      if (filter === 'all') {
        const allLeads = data.leads || [];
        const newCounts = {
          all: allLeads.length,
          new: allLeads.filter(l => l.status === 'new').length,
          contacted: allLeads.filter(l => l.status === 'contacted').length,
          qualified: allLeads.filter(l => l.status === 'qualified').length,
          booked: allLeads.filter(l => l.status === 'booked').length,
          lost: allLeads.filter(l => l.status === 'lost').length
        };
        setCounts(newCounts);
      }
    } catch (err) {
      console.error('Failed to load leads:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search
  const filteredLeads = leads.filter(lead => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      lead.client_name?.toLowerCase().includes(searchLower) ||
      lead.client_email?.toLowerCase().includes(searchLower) ||
      lead.trip_summary?.destination?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div>
      {/* Page Header */}
      <div className="b2b-page-header">
        <div>
          <h1 className="b2b-page-title">Leads</h1>
          <p className="b2b-page-subtitle">
            {counts.all || 0} total leads • {counts.new || 0} new this month
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input
            type="text"
            className="b2b-form-input"
            placeholder="🔍 Search leads..."
            style={{ width: '250px' }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="b2b-btn b2b-btn-secondary">Export</button>
        </div>
      </div>

      {/* Leads Panel */}
      <div className="b2b-panel">
        {/* Filters */}
        <div className="b2b-leads-filters">
          {FILTERS.map(f => (
            <button
              key={f.key}
              className={`b2b-filter-btn ${filter === f.key ? 'active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label} {counts[f.key] !== undefined && `(${counts[f.key]})`}
            </button>
          ))}
        </div>

        {/* Leads List */}
        {loading ? (
          <div className="b2b-loading">
            <div className="b2b-spinner" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="b2b-empty-state">
            <div className="b2b-empty-state-icon">👥</div>
            <div className="b2b-empty-state-title">
              {search ? 'No matching leads' : 'No leads yet'}
            </div>
            <div className="b2b-empty-state-desc">
              {search 
                ? 'Try a different search term' 
                : 'Install the widget on your website to start capturing leads'}
            </div>
          </div>
        ) : (
          <div className="b2b-leads-list" style={{ maxHeight: 'none' }}>
            {filteredLeads.map(lead => (
              <div
                key={lead.id}
                className={`b2b-lead-item ${lead.status === 'new' ? 'unread' : ''}`}
                onClick={() => navigate(`/b2b/leads/${lead.id}`)}
              >
                {/* Score */}
                <div className={`b2b-lead-score ${getScoreClass(lead.score || 0)}`}>
                  {lead.score || 0}
                </div>
                
                {/* Avatar */}
                <div className="b2b-lead-avatar">
                  {getInitials(lead.client_name)}
                </div>
                
                {/* Info */}
                <div className="b2b-lead-info">
                  <div className="b2b-lead-name">{lead.client_name || 'Anonymous'}</div>
                  <div className="b2b-lead-destination">
                    {lead.client_email || 'No email'} {lead.client_phone && `• ${lead.client_phone}`}
                  </div>
                </div>
                
                {/* Trip Details */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', color: '#18181b' }}>
                    {typeof lead.trip_summary?.destination === 'string' 
                      ? lead.trip_summary.destination 
                      : 'No destination'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#71717a' }}>
                    {typeof lead.trip_summary?.travel_dates === 'string' 
                      ? lead.trip_summary.travel_dates 
                      : lead.trip_summary?.travel_dates?.start 
                        ? `${lead.trip_summary.travel_dates.start}${lead.trip_summary.travel_dates.end ? ' - ' + lead.trip_summary.travel_dates.end : ''}`
                        : 'No dates'} 
                    {lead.trip_summary?.travelers && ` • ${lead.trip_summary.travelers}`}
                  </div>
                </div>
                
                {/* Meta */}
                <div className="b2b-lead-meta">
                  <div className="b2b-lead-time">{formatTimeAgo(lead.created_at)}</div>
                  <div className={`b2b-lead-status ${lead.status}`}>
                    {lead.status === 'new' ? 'New' : 
                     lead.status === 'contacted' ? 'Contacted' :
                     lead.status === 'qualified' ? 'Qualified' :
                     lead.status === 'booked' ? 'Booked!' : 
                     lead.status === 'lost' ? 'Lost' : lead.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
